import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNotification } from "../contextApi/NotificationProvider";
import { motion } from "framer-motion";
import {
  getUserDetails,
  updateUserDetails,
} from "../redux/ReduxSlices/authSlice";
import { template, customTemplate } from "../utils/EmailTemplates";
import { FiEdit2, FiLoader } from "react-icons/fi";
import { IoMdClose } from "react-icons/io";
import { useForm } from "react-hook-form";
import {
  addCustomTemplate,
  editCustomTemplate,
  getCustomTemplate,
} from "../redux/ReduxSlices/customeTemplateSlice";

const EmailTemplateSelector = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const { userData, loading } = useSelector((state) => state.auth);
  const [customTemplateData, setCustomTemplateData] = useState({});
  const [customRenderedHtml, setCustomRenderedHtml] = useState(null);
  const [hasCustomTemplate, setHasCustomTemplate] = useState(false);
  const [customTemplateId, setCustomTemplateId] = useState();
  const [activeTab, setActiveTab] = useState("system");
  const rawCompanyName = userData?.company_info?.company_name || "";
  const companyName =
    rawCompanyName.charAt(0).toUpperCase() + rawCompanyName.slice(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm();

  const { showNotification } = useNotification();
  const dispatch = useDispatch();
  useEffect(() => {
    setTemplates(template);
    setIsLoading(false);
  }, []);

  const handleTemplateUse = (template) => {
    setSelectedTemplate(template);
    setShowConfirmModal(true);
  };

  const handleConfirmSelection = async () => {
    if (selectedTemplate) {
      const resultAction = await dispatch(
        updateUserDetails({
          company: {
            selected_email_template: selectedTemplate.id,
          },
        })
      );

      if (updateUserDetails.fulfilled.match(resultAction)) {
        showNotification(
          "Email template updated successfully!",
          "success",
          3000
        );
        await dispatch(getUserDetails());
      } else {
        showNotification(
          resultAction.payload || "Failed to update email template",
          "error",
          3000
        );
      }

      setShowConfirmModal(false);
    }
  };

  const submitTemplate = async (data) => {
    try {
      const payload = {
        name: "My Custom Template",
        header: data?.header ? data.header : "",
        subheader: data?.subHeader ? data.subHeader : "",
        body: data?.content ? data.content : "",
        footer: data?.closingStatement ? data.closingStatement : "",
      };
      let response;
      if (hasCustomTemplate) {
        response = await dispatch(
          editCustomTemplate({ payload, customTemplateId })
        ).unwrap();
        if (response.status == "success") {
          showNotification(
            "Email template updated successfully!",
            "success",
            3000
          );
        }
        await getCustomEmailTemplates();
      } else if (!hasCustomTemplate) {
        response = await dispatch(addCustomTemplate(payload)).unwrap();
        if (response.status == "success") {
          showNotification(
            "Email template added successfully!",
            "success",
            3000
          );
        }
      }
    } catch (error) {
      console.log("errr", error);
      showNotification("Failed to update email template", "error", 3000);
    }
    setCustomTemplateData(data);
    setHasCustomTemplate(true);
    setShowTemplateForm(false);
  };

  const getCustomEmailTemplates = async () => {
    try {
      let response = await dispatch(getCustomTemplate()).unwrap();
      if (response.status == "success") {
        let data = {
          header: response.data[0].header,
          subHeader: response.data[0].subheader,
          content: response.data[0].body,
          closingStatement: response.data[0].footer,
        };
        setCustomTemplateId(response.data[0].email_template_pk);
        setValue("header", data.header);
        setValue("subHeader", data.subHeader);
        setValue("content", data.content);
        setValue("closingStatement", data.closingStatement);
        setCustomTemplateData(data);
        setHasCustomTemplate(true);
      }
    } catch (error) {
      console.log("err", error);
    }
  };

  useEffect(() => {
    getCustomEmailTemplates();
  }, []);

  // const hasCustomTemplate = Object.keys(customTemplateData).length > 0;
  useEffect(() => {
    if (hasCustomTemplate && Object.keys(customTemplateData)?.length > 0) {
      let html = customTemplate.content;
      html = html.replaceAll("{header}", customTemplateData.header || "");
      html = html.replaceAll(
        "{sub_header}",
        customTemplateData.subHeader || ""
      );
      html = html.replaceAll("{content}", customTemplateData.content || "");
      html = html.replaceAll(
        "{closing_statement}",
        customTemplateData.closingStatement || ""
      );
      html = html.replaceAll("{Company Name}", companyName || "");

      setCustomRenderedHtml(html);
    }
  }, [hasCustomTemplate, customTemplateData]);

  if (isLoading) {
    return <div className="text-center py-8">Loading templates...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center py-8">Error: {error}</div>;
  }

  return (
    <div className="container mx-auto px-10 max-md:px-4 py-8">
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}
      <div className="flex flex-col md:flex-row justify-between">
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h1 className="text-2xl font-bold text-gray-800 max-md:text-xl mb-1 max-md:ml-12 max-md:mb-4">
            Select an Email Template for Existing Employee
          </h1>
          <p className="text-gray-400 text-large mb-6 max-md:text-start">
            Customize your message by choosing an email template before sending
            the assessment.{" "}
          </p>
        </motion.div>
        <div className="flex bg-gray-200 rounded-full p-1 w-full max-w-md mx-auto md:mx-0 h-[50%]">
          <button
            onClick={() => setActiveTab("system")}
            className={`flex-1 py-2 text-sm rounded-full transition-all ${activeTab === "system"
              ? "bg-white shadow text-black"
              : "text-gray-500"
              }`}
          >
            System Templates
          </button>
          <button
            onClick={() => setActiveTab("custom")}
            className={`flex-1 py-2 text-sm rounded-full transition-all font-semibold relative ${activeTab === "custom"
              ? "bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg"
              : "bg-gradient-to-r from-violet-400 to-purple-500 text-white shadow-md animate-pulse"
              }`}
          >
            Add Your Custom Template
          </button>
        </div>
      </div>

      {activeTab == "custom" && !hasCustomTemplate && (
        <div className="p-4 bg-violet-50 rounded-lg border border-violet-100 shadow-sm hover:shadow-md transition-shadow">
          <button
            className=" px-6 py-3 text-lg btn-gradient text-whitetransition-all rounded-md font-medium"
            onClick={() => setShowTemplateForm(true)}
          >
            Add Custom Template
          </button>
        </div>
      )}

      <motion.div
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 mt-10 md:mt-0"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {activeTab == "custom"
          ? hasCustomTemplate &&
          customRenderedHtml && (
            <div
              className={`rounded-xl border flex flex-col h-[800px] transition ${userData?.company_info?.selected_email_template === 4
                ? "ring-1 ring-green-600 border-green-600 border-2"
                : ""
                }`}
            >
              {/* <button
                  className="px-3 py-1 md:px-4 md:py-1.5 text-sm bg-violet-600 text-white hover:bg-violet-700 transition rounded-md mb-3"
                  onClick={() => setShowTemplateForm(true)}
                >
                  Edit Template
                </button> */}
              <div className="px-4 py-2 text-black rounded-t-xl"></div>

              <div
                className="email-preview p-2 overflow-y-auto flex-1 bg-gray-50"
                dangerouslySetInnerHTML={{ __html: customRenderedHtml }}
              />

              <div className="p-4 flex justify-center gap-4">
                <button
                  onClick={() => handleTemplateUse(customTemplate)}
                  className={`px-8 py-1.5 rounded-md text-sm text-black transition ${userData?.company_info?.selected_email_template === 4
                    ? "bg-green-600 hover:bg-green-700 text-white"
                    : "border border-violet-600"
                    }`}
                >
                  {userData?.company_info?.selected_email_template === 4
                    ? "Currently Used"
                    : "Select"}
                </button>
                {userData?.company_info?.selected_email_template === 4 && (
                  <button
                    onClick={() => setShowTemplateForm(true)}
                    className="px-6 py-2.5 bg-violet-600 hover:bg-violet-700 text-sm text-white rounded-lg font-medium transition-all duration-200 shadow-md hover:shadow-lg flex items-center gap-2"
                  >
                    <FiEdit2 className="w-5 h-5" />
                    <span>Edit Template</span>
                  </button>
                )}
              </div>
            </div>
          )
          : templates.map((temp) => {
            const isSelected =
              userData?.company_info?.selected_email_template === temp.id;

            const renderedHtml = temp.content.replaceAll(
              "{Company Name}",
              companyName || ""
            );

            return (
              <div
                key={temp.id}
                className={`rounded-xl border flex flex-col h-[800px] transition ${isSelected
                  ? "ring-1 ring-green-600 border-green-600 border-2"
                  : ""
                  }`}
              >
                <div className="px-4 py-2 text-black rounded-t-xl"></div>

                <div
                  className="email-preview p-2 overflow-y-auto flex-1 bg-gray-50"
                  dangerouslySetInnerHTML={{ __html: renderedHtml }}
                />

                <div className="p-4 flex justify-center">
                  <button
                    onClick={() => handleTemplateUse(temp)}
                    className={`px-8 py-1.5 rounded-md text-black transition ${isSelected
                      ? "bg-green-600 hover:bg-green-700 text-white"
                      : "border border-violet-600"
                      }`}
                  >
                    {isSelected ? "Currently Used" : "Select"}
                  </button>
                </div>
              </div>
            );
          })}

        {showConfirmModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm p-4">
            <div className="relative bg-white p-8 rounded-md shadow-xl w-[380px] space-y-2 text-center">
              <h2 className="text-lg flex items-center justify-center gap-2">
                Are you sure you want to select this template?
              </h2>
              <div className="flex justify-evenly">
                <button
                  className="bg-[#f5f3ff] text-violet-500 py-2 px-6 rounded-lg mt-4 hover:bg-violet-500 hover:text-white transition-all"
                  onClick={handleConfirmSelection}
                >
                  Yes
                </button>
                <button
                  className="bg-[#f5f3ff] text-violet-500 py-2 px-6 rounded-lg mt-4 hover:bg-violet-500 hover:text-white transition-all"
                  onClick={() => setShowConfirmModal(false)}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}
      </motion.div>

      <motion.div
        className="bg-violet-50 mt-12 border  border-violet-200 rounded-xl p-5 mb-6 text-gray-600 text-sm leading-relaxed space-y-3"
        initial={{ opacity: 0, y: 50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        <p className="text-2xl font-bold text-gray-800 text-start max-md:text-xl mb-6 max-md:ml-8">
          About Email Templates
        </p>

        <ul className="list-disc list-inside space-y-2 text-base mt-4">
          <li className="">
            <span className="font-semibold text-black">
              Custom Look & Feel :{" "}
            </span>
            Choose how your email will appear to assessment recipients—whether
            it feels formal, friendly, or direct.
          </li>
          <li className="">
            <span className="font-semibold text-black">
              Consistent Messaging :{" "}
            </span>
            Ensure all candidates receive a uniform email design and tone, no
            matter who sends the assessment.
          </li>
          <li className="">
            <span className="font-semibold text-black">Time-Saver : </span>
            Selecting a template streamlines your process—no need to write or
            format emails manually for each send.
          </li>
          <li className="">
            <span className="font-semibold text-black">
              Flexible Selection :{" "}
            </span>
            You can update your email style anytime by selecting a different
            template. Your most recently selected template will be used as the
            default for all future assessment emails.
          </li>
          <li className="">
            <span className="font-semibold text-black">
              Preview Before You Pick :{" "}
            </span>
            You’ll see a full preview of each design before making your
            choice—so you can be confident in your selection.
          </li>
        </ul>

        <p className="text-gray-500 text-base">
          This template will be automatically applied when sending assessments.
          You can return to this page anytime to switch to a different template.
        </p>
      </motion.div>
      {showTemplateForm && (
        <div className="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black bg-opacity-50 z-50 p-4">
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-800">
                Create Custom Template
              </h3>
              <button
                onClick={() => setShowTemplateForm(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <IoMdClose size={24} />
              </button>
            </div>

            <form
              onSubmit={handleSubmit(submitTemplate)}
              className="p-6 space-y-4"
            >
              <div>
                <label
                  htmlFor="header"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Template Header <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="header"
                  name="header"
                  {...register("header", {
                    required: "Header is required",
                  })}
                  // value={formData.header}
                  // onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.header && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.header.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="subHeader"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Template Sub-header{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="subHeader"
                  name="subHeader"
                  {...register("subHeader", {
                    required: "Sub-Header is required",
                  })}
                  // value={formData.subHeader}
                  // onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.subHeader && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.subHeader.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="content"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Template Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  id="content"
                  name="content"
                  rows={4}
                  {...register("content", {
                    required: "Content is required",
                  })}
                  // value={formData.content}
                  // onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.content && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.content.message}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="closingStatement"
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Email Template Closing Statement{" "}
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="closingStatement"
                  name="closingStatement"
                  {...register("closingStatement", {
                    required: "Closing statement is required",
                  })}
                  // value={formData.closingStatement}
                  // onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                {errors.closingStatement && (
                  <p className="mt-1 text-sm text-red-600">
                    {errors.closingStatement.message}
                  </p>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowTemplateForm(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-md hover:bg-violet-700"
                >
                  {!hasCustomTemplate ? "Create Template" : "Edit Template"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmailTemplateSelector;
